import * as React from "react";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { addWeeks } from "../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { FrequencyTypeArrays, FrequencyArraysMap, datePickerOptions } from "../../Resource/Contents";
import { SectionHeader } from "../../SharedComponents";
import { BusinessDaysAction } from "../../../../Common/BusinessDays/Actions/BusinessDaysAction";
import { Action } from "../../../../../Actions/Action";
import { PensionSubAccountSettings } from "../../../../../../../Wrap/Models/Domains/SubAccount/PensionSubAccountSettings";
import { GetRegularCrystallisationAction } from "../../../CancelRegularCrystallisationWizard/Actions/GetRegularCrystallisationAction";
import { RegularDrawdown } from "../../../../../../../Wrap/Models/Domains/Drawdown/RegularDrawdown";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { PensionLevelDrawdownIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/PensionLevelDrawdownIncome";

interface IRegularCrystallisationProps extends IProcessComponentProps {
    data: DrawdownModel;
    process: ProcessType;
    doCalculation: VoidFunction;
    hierarchyId?: string;
    minimumDate?: Date;
}

interface IRegularCrystallisationState {
    minimumDate?: Date;
    isLoading?: boolean;
    pensionSubAccountSettings?: PensionSubAccountSettings;
    drawdownincome?: PensionLevelDrawdownIncome;
    showWarning?: boolean;
    regularDrawdown?: RegularDrawdown;
    showNoRegularIncomeMsg?: boolean;
    showRegularIncomeMsg?: boolean;
}

export class RegularCrystallisation extends ProcessComponent<
    IRegularCrystallisationProps,
    IRegularCrystallisationState
> {
    initState() {
        this.state = {
            showWarning: false
        }
    }
    constructor(props: any) {
        super(props);
        this.state = {
            isLoading: false,
        };
    }

    private onChangeRegularCrystallisation(changes) {
        this.onDataChange(this.props.onChange, changes, this.props.data, "RegularCrystallisationDetails");
    }
    
    componentDidMount() {
        const { data } = this.props;
        super.componentDidMount();
        if (!data?.RegularCrystallisationDetails?.FirstPaymentDay) {
            this.onChangeRegularCrystallisation({
                FirstPaymentDay: JsonDateParser.toJsonDateNoOffset(addWeeks(new Date(), 2)),
            });
        }

        BusinessDaysAction.GetBusinessDays(new Date(), true, 10, true, (callBack: Date) => {
            this.setState({
                minimumDate: callBack,
                isLoading: true,
            });
        });

        const hierarchyId =
            this.props.process == ProcessType.Quote ? this.props.data?.HierarchyId : this.props?.hierarchyId;
        Action.getPensionSubAccountSettings(hierarchyId, false, (val) => {
            this.setState(
                {
                    pensionSubAccountSettings: val,
                },
                () => {
                    if (this.state.pensionSubAccountSettings?.HasRegularWithdrawal) {
                        Action.getDrawdownIncome(this.props.hierarchyId, (val) => {
                            this.setState({
                                drawdownincome: val,
                            });
                        });
                    }
                }
            );
            this.getEditIncomeDrawdownMsg();
        });

        if (this.props.process != ProcessType.Quote && this.props.process != ProcessType.NB) {
            GetRegularCrystallisationAction.getRegularCrystallisation(hierarchyId, (result) =>
                this.setState({ regularDrawdown: result })
            );
        }

        // set the showWarning flag to true if the frequency is different from the default frequency
        this.setState({ showWarning: this.state.drawdownincome?.AnnualIncome?.Frequency !== this.getFrequency() });
    }

    private getInitialCrystallisationAmount(): number {
        const { data } = this.props;
        if (
            this.props.process != ProcessType.Quote &&
            this.props.process != ProcessType.NB &&
            this.props.process != ProcessType.EditIncomeDrawdown &&
            this.props.process != ProcessType.TakePensionBenefits &&
            this.props.process != ProcessType.WhatIf
        ) {
            return this.state.regularDrawdown?.IncomeRequired?.Amount;
        }
        return (
            data?.RegularCrystallisationDetails?.Amount?.Amount == null? this.state.regularDrawdown?.IncomeRequired?.Amount :data?.RegularCrystallisationDetails?.Amount?.Amount
        );
    }

    private getFrequency(): Frequency {
        const { data } = this.props;
        if (
            this.props.process != ProcessType.Quote &&
            this.props.process != ProcessType.NB &&
            this.props.process != ProcessType.EditIncomeDrawdown &&
            this.props.process != ProcessType.TakePensionBenefits &&
            this.props.process != ProcessType.WhatIf
        ) {
            return this.state.regularDrawdown?.Frequency;
        }
        return (
            this.state.drawdownincome?.AnnualIncome?.Frequency ||
            data?.RegularCrystallisationDetails?.Frequency ||
            this.state.regularDrawdown?.Frequency
        );
    }

    private getDateSelection(data): Date {

        let calculatedDate = JsonDateParser.toDate(
            this.state.drawdownincome?.AnnualIncome?.FirstPayment ||
            data?.RegularCrystallisationDetails?.FirstPaymentDay ||
            (JsonDateParser.toDate(this.state.regularDrawdown?.NextPaymentDate) >
            this.state.minimumDate
                ? this.state.regularDrawdown?.NextPaymentDate
                : this.state.minimumDate)
        );

        calculatedDate = this.setDayToUnder29(calculatedDate);
        return calculatedDate;
    }

    private getMinimumDateForErrorMessage(): string {
        let displayDate = JsonDateParser.toDate(this.state.minimumDate);
        displayDate = this.setDayToUnder29(displayDate);
        // now we need to subtact one to make the message `after` minimum date
        displayDate.setDate(displayDate.getDate() - 1);
        return JsonDateParser.getFormatDateString(displayDate, "dd MM yyyy", true, true);
    }

    private setDayToUnder29(date): Date {
        if (date.getDate() > 28) {
            date = new Date(date.getFullYear(), date.getMonth()+1, 1);
        }

        return date;
    }

    private getEditIncomeDrawdownMsg() {
        if (this.state.pensionSubAccountSettings?.HasRegularWithdrawal) {
            this.setState({ showNoRegularIncomeMsg: false });
            BusinessDaysAction.GetBusinessDays(
                JsonDateParser.toDateNoOffset(this.state?.drawdownincome?.AnnualIncome?.NextPayment),
                true,
                10,
                false,
                (callBack: Date) => {
                    if (
                        new Date(callBack).getTime() <= new Date(new Date().setHours(0, 0, 0, 0)).getTime() &&
                        JsonDateParser.toDateNoOffset(
                            this.state?.drawdownincome?.AnnualIncome?.NextPayment
                        ).getTime() >= new Date(new Date().setHours(0, 0, 0, 0)).getTime()
                    ) {
                        this.setState({ showRegularIncomeMsg: true });
                    }
                }
            );
        } else {
            BusinessDaysAction.GetBusinessDays(
                JsonDateParser.toDateNoOffset(this.state.pensionSubAccountSettings?.NextRegularCrystallisationDate),
                true,
                10,
                false,
                (callBack: Date) => {
                    let flag = new Date(callBack).getTime() <= new Date(new Date().setHours(0, 0, 0, 0)).getTime();
                    if (flag) {
                        BusinessDaysAction.GetBusinessDays(
                            JsonDateParser.toDateNoOffset(
                                this.state.pensionSubAccountSettings?.NextRegularCrystallisationDate
                            ),
                            true,
                            5,
                            false,
                            (callBack: Date) => {
                                flag =
                                    new Date(callBack).getTime() >= new Date(new Date().setHours(0, 0, 0, 0)).getTime();
                                this.setState({ showNoRegularIncomeMsg: flag });
                            }
                        );
                    }
                }
            );
        }
    }

    private renderRegularCrystallisationDetails(): JSX.Element {
        const { data, process } = this.props;

        if (!this.state.isLoading) {
            return null;
        }

        return (
            <>
                {this.props.process != ProcessType.EditIncomeDrawdown && (
                    <RadiosEditor
                        options={[true, false]}
                        cssWrapper={"rg-inline mb-10"}
                        onChange={(v) =>
                            this.onChangeRegularCrystallisation({
                                HasADifferentInitialAmount: v,
                                InitialAmount: null,
                                Amount: {
                                    Amount:
                                        data?.RegularCrystallisationDetails?.Amount?.Amount ||
                                        this.state.regularDrawdown?.IncomeRequired?.Amount,
                                    Currency: CurrencyCode.GBP,
                                },
                                Frequency:
                                    this.state.drawdownincome?.AnnualIncome?.Frequency ||
                                    data?.RegularCrystallisationDetails?.Frequency ||
                                    this.state.regularDrawdown?.Frequency ||
                                    null,
                            })
                        }
                        onBlur={(v) =>
                            data?.RegularCrystallisationDetails?.HasADifferentInitialAmount
                                ? null
                                : this.props.doCalculation()
                        }
                        selected={data?.RegularCrystallisationDetails?.HasADifferentInitialAmount}
                        label="Specify a different initial drawdown amount?"
                        getText={(o) => (o ? "Yes" : "No")}
                        required={"Please select"}
                    />
                )}
                {data?.RegularCrystallisationDetails?.HasADifferentInitialAmount && (
                    <MoneyTextBoxEditor
                        cssWrapper="control-size-245"
                        label="Initial tailored drawdown amount"
                        currency={CurrencyCode.GBP}
                        value={data?.RegularCrystallisationDetails?.InitialAmount?.Amount}
                        required={"Please enter"}
                        onChange={(v) =>
                            this.onChangeRegularCrystallisation({
                                InitialAmount: { Amount: v, Currency: CurrencyCode.GBP },
                            })
                        }
                        onBlur={(v) => this.props.doCalculation()}
                        validationRules={[
                            {
                                errorMessage: "Please enter an amount that is greater than zero.",
                                validator: (v) => v > 0,
                            },
                        ]}
                    />
                )}
                <MoneyTextBoxEditor
                    cssWrapper="control-size-245"
                    label="Tailored drawdown amount"
                    currency={CurrencyCode.GBP}
                    value={this.getInitialCrystallisationAmount()}
                    required={"Please enter"}
                    onChange={(v) =>
                        this.onChangeRegularCrystallisation({ Amount: { Amount: v, Currency: CurrencyCode.GBP } })
                    }
                    onBlur={(v) => {
                        if (this.validateRulesForRegularCrystallisation(v)) {
                            this.props.doCalculation();
                        }
                    }}
                    validationRules={[
                        {
                            errorMessage:
                                "Please enter an amount that is greater than zero.",
                            validator: (v) =>
                                (v == 0 && process != ProcessType.EditIncomeDrawdown && process != ProcessType.WhatIf)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage: "Please enter an amount less than or equal to the maximum Value available to drawdown: £" 
                            + this.props.data.CrystallisationDetails.AvailableToCrystallise.Amount,
                            validator: (v) => v <= this.props.data.CrystallisationDetails.AvailableToCrystallise.Amount
                        }
                    ]}
                />
                <SelectEditor
                    options={FrequencyTypeArrays}
                    getText={(v) => {
                        return FrequencyArraysMap[v];
                    }}
                    cssWrapper="control-size-245"
                    required="Please select payment frequency"
                    placeholder="Please select"
                    selected={this.getFrequency()}
                    label="Payment frequency"
                    onChange={(v) => {
                        this.setState({ showWarning: false });
                        const defaultSelected = this.state.drawdownincome?.AnnualIncome?.Frequency;
                        if (defaultSelected !== v) {
                            this.setState({ showWarning: true });
                        }
                        this.onChangeRegularCrystallisation({ Frequency: v, HasADifferentInitialFrequency: defaultSelected !== v });
                    }}
                    onBlur={(v) => this.props.doCalculation()}
                />
                {(this.state.showWarning || data?.RegularCrystallisationDetails?.HasADifferentInitialFrequency) && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={
                            <>
                                <p>
                                    Changing the payment date / frequency will amend any regular pension income also
                                    associated with this pension.
                                </p>
                                <p>
                                    Please review the income required from the pension through 'Edit Income Payments'
                                    process following acceptance of this instruction.
                                </p>
                            </>
                        }
                    />
                )}
                <div className="row">
                    <div className="col-md-4">
                        <DatePicker
                            filterDate={{
                                errorMessage: "Date must also be between 1-28 of any month",
                                validator: (v) => {
                                    if (v.getDate() > 28) {
                                        return false;
                                    }
                                    return true;
                                },
                            }}
                            label="First payment date"
                            selected={ JsonDateParser.toDateNoOffset(this.getDateSelection(data)) }
                            atLeast={{
                                errorMessage: this.props.minimumDate
                                    ? "Please enter a date after " + this.getMinimumDateForErrorMessage()
                                    : "Please enter a date that is at least 10 working days from today.",
                                value: new Date(this.state.minimumDate ?? this.props.minimumDate),
                            }}
                            onChange={(v) =>
                                this.onChangeRegularCrystallisation({
                                    FirstPaymentDay: JsonDateParser.toJsonDateNoOffset(v),
                                })
                            }
                            cssWrapper="control-size-245"
                            alwaysValidateOnUpdate={false}
                            {...datePickerOptions}
                            required="Please select first payment date"
                        />
                    </div>
                </div>
                {this.props.process === ProcessType.EditIncomeDrawdown && this.state.showNoRegularIncomeMsg && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={
                            <>
                                <p>Please note: we're due to pay the current drawdown soon.</p>
                                <p>
                                    You can still make changes but they will take effect after the current one has
                                    completed.
                                </p>
                            </>
                        }
                    />
                )}
                {this.props.process === ProcessType.EditIncomeDrawdown && this.state.showRegularIncomeMsg && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={
                            <>
                                <p>
                                    Please note: we're due to pay the current drawdown and regular pension income
                                    soon.
                                </p>
                                <p>
                                    You can still make changes but they will take effect after the current one has
                                    completed.
                                </p>
                            </>
                        }
                    />
                )}
            </>
        );
    }

    private validateRulesForRegularCrystallisation(value: number) {
        const { data ,process} = this.props;
        const { CrystallisationDetails } = data;
        if ((value == 0 && process != ProcessType.EditIncomeDrawdown  && process != ProcessType.WhatIf) || value > CrystallisationDetails?.AvailableToCrystallise?.Amount) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        return (
            <>
                {SectionHeader("Tailored drawdown details")}
                {this.renderRegularCrystallisationDetails()}
            </>
        );
    }
}
